import { v4 as uuidv4 } from 'uuid';
import { computed, inject } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

import { Routes } from '~/constants/routes';
import { AddToastNotification } from '~/constants/mutations';
import { thumbnailCache } from '~/services/thumbnail-cache-service';
import { fetchDocumentThumbnails } from '~/utils/thumbnail-cache-utils';
import { keyBy, merge, sortBy, uniq, values } from 'lodash';

import useModals from '~/composables/useModals';
import {
  AddDownloadInProgress,
  DeleteAuthoredDocumentById,
  RemoveDownloadInProgress,
  SetAuthoredDocuments,
  SetSelectedAuthoredDocument,
  SetSelectedReferencePanel,
  SetTotalMediaImages,
  UpdateMessageReferences,
  UpdateSystemMessageTranslationData,
} from '~/constants/actions';
import {
  GetAuthoredDocuments,
  GetSelectedAuthoredDocument,
  GetSelectedReference,
  GetTotalMediaImages,
  IsDownloadInProgress,
} from '~/constants/getters';
import { apiGatewayEndpoints } from '~/services/arcane-api-gateway/api-gateway-endpoints';
import { mapMetadataFields } from '~/utils/document-utils';

export default () => {
  const store = useStore();
  const router = useRouter();
  const arcaneApiGateway = inject('arcaneApiGateway');
  const { showPreviewDocument } = useModals();

  const authoredDocuments = computed(() => store.getters[`documentStore/${GetAuthoredDocuments}`]);
  const selectedAuthoredDocument = computed(() => store.getters[`documentStore/${GetSelectedAuthoredDocument}`]);
  const selectedReference = computed(() => store.getters[`documentStore/${GetSelectedReference}`]);
  const totalMediaImages = computed(() => store.getters[`documentStore/${GetTotalMediaImages}`]);

  const setAuthoredDocuments = (data) => store.dispatch(`documentStore/${SetAuthoredDocuments}`, data);

  const setTotalMediaImages = (data) => store.dispatch(`documentStore/${SetTotalMediaImages}`, data);

  const setSelectedAuthoredDocument = (data) => store.dispatch(`documentStore/${SetSelectedAuthoredDocument}`, data);

  const setMessageReferencesThumbs = async (messageId, data) => {
    const { references = [], citedReferences = [] } = data;

    for (const reference of references) {
      const { pageNumber } = reference;

      // transform the STRING items to INT, so we can sort and remove duplicates
      let pages = [1, ...(pageNumber ? pageNumber : [])].map((item) => Number(item));
      pages = sortBy(uniq(pages));

      reference.pageNumber = pages;
    }

    const documents = references.map(({ externalId, pageNumber }) => {
      return { id: externalId, pages: pageNumber };
    });

    const response = await getDocumentThumbs({ documents });
    const updatedReferences = values(merge(keyBy(response, 'documentId'), keyBy(references, 'externalId')));
    const updatedCitedReferences = citedReferences.map((citedRef) => {
      const responseData = response.find((resp) => resp.documentId === citedRef.externalId);
      return responseData ? { ...citedRef, ...responseData } : citedRef;
    });

    store.dispatch(`conversationStore/${UpdateMessageReferences}`, {
      messageId,
      data: { ...data, references: updatedReferences, citedReferences: updatedCitedReferences },
    });
  };

  const updateSystemMessageTranslationData = async (messageId, data) => {
    const response = await getDocumentThumbs({ documents: [{ id: data.externalId, pages: [1] }] });
    const newData = { ...data, pages: response[0].pages };
    store.dispatch(`conversationStore/${UpdateSystemMessageTranslationData}`, { messageId, data: [newData] });
  };

  const getDocumentThumbs = async ({ documents = [], size = 'small', service }) => {
    try {
      const fetchFromApi = async (uncachedDocuments) => {
        if (service) {
          const { data } = await service.post(apiGatewayEndpoints.s3Docs.pageCovers, {
            documents: uncachedDocuments,
          });
          return data;
        }
        const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.s3Docs.pageCovers, {
          documents: uncachedDocuments,
        });
        return data;
      };

      return await fetchDocumentThumbnails(documents, fetchFromApi, size);
    } catch (error) {
      console.error('Failed to load documents thumbs:', error);
      return [];
    }
  };

  const setSelectedDocument = async (reference, isMetadata = true) => {
    let data;
    if (isMetadata && reference.documentType !== 'tempDocument') {
      const { externalId } = reference;
      ({ data } = await arcaneApiGateway.post(apiGatewayEndpoints.documents.metadata, {
        externalIds: [externalId],
      }));
    }

    const metadataFields = data ? data[0] : {};

    if (!reference.pages) {
      reference.pages = {};
      reference.pages[1] = {};
    }

    const largeThumb = await getDocumentThumbs({
      documents: [{ id: reference.externalId, pages: [1] }],
      size: 'large',
    });
    reference.pages[1].large = largeThumb[0].pages[1].large;

    store.dispatch(`documentStore/${SetSelectedReferencePanel}`, mapMetadataFields(reference, metadataFields));
  };

  const getSourceTranslationAvailable = async (externalId) => {
    const {
      data: { result },
    } = await arcaneApiGateway.post(apiGatewayEndpoints.translation.sourceTranslationAvailable, {
      externalId,
    });
    return result;
  };

  const getPdfUrl = async (externalId, languageCode) => {
    try {
      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.s3Docs.pdfUrl, {
        documentId: externalId,
        language: languageCode,
      });
      return data.url;
    } catch (error) {
      console.error('Failed to load PDF source:', error);
    }
  };

  const getSourceUrl = async (externalId, languageCode) => {
    try {
      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.s3Docs.sourceUrl, {
        documentId: externalId,
        language: languageCode,
      });
      return data.url;
    } catch (error) {
      console.error('Failed to load zip source:', error);
    }
  };

  const getAuthoredDocumentThumbs = async (documents = []) => {
    try {
      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.s3Docs.thumbnail, {
        documents,
      });
      return data;
    } catch (error) {
      console.error('Failed to load documents thumbs:', error);
      return [];
    }
  };

  const getAuthoredDocumentImportedThumbs = async (externalIds = []) => {
    try {
      // Simple caching for imported images
      const uncachedIds = [];
      const cachedResults = [];

      for (const externalId of externalIds) {
        const cachedUrl = thumbnailCache.get(externalId, 'imported', 'original');
        if (cachedUrl) {
          cachedResults.push({
            id: externalId,
            url: cachedUrl,
            name: 'original.png',
            variant: 'original',
          });
        } else {
          uncachedIds.push(externalId);
        }
      }

      if (uncachedIds.length === 0) {
        return cachedResults;
      }

      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.s3Docs.thumbnailImportedImages, {
        externalIds: uncachedIds,
      });

      for (const image of data) {
        if (image.url && image.variant) {
          thumbnailCache.set(image.id, 'imported', image.url, image.variant);
        }
      }

      return [...cachedResults, ...data];
    } catch (error) {
      console.error('Failed to load documents thumbs:', error);
      return [];
    }
  };

  const getAuthoredDocuments = async () => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.authoredDocuments.getAuthoredDocuments);
      const documents = (data || []).map(({ id, externalId }) => ({ id, externalId }));
      const thumbs = await getAuthoredDocumentThumbs(documents);
      const updatedDocuments = values(merge(keyBy(data, 'id'), keyBy(thumbs, 'id')));
      setAuthoredDocuments(updatedDocuments);

      return data.data;
    } catch (error) {
      console.log('Failed to save Authored Document', error);
    }
  };

  const getAuthoredDocumentById = async (id) => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.authoredDocuments.getAuthoredDocumentById(id));

      return data[0];
    } catch (error) {
      console.log('Failed to load Authored Document', error);
      return [];
    }
  };

  const deleteAuthoredDocumentById = async (id) => {
    try {
      await arcaneApiGateway.delete(apiGatewayEndpoints.authoredDocuments.deleteAuthoredDocumentById(id));

      // Remove the document from the local store state
      store.dispatch(`documentStore/${DeleteAuthoredDocumentById}`, id);
      return id;
    } catch (error) {
      console.log('Failed to delete Authored Document', error);
      return null;
    }
  };

  const downloadAuthoredDocument = async (id) => {
    // Check if download is already in progress for this document
    if (store.getters[`documentStore/${IsDownloadInProgress}`](id)) {
      console.log('Download already in progress for this document');
      return '';
    }

    try {
      // Add document to downloads in progress
      const response = await arcaneApiGateway.get(apiGatewayEndpoints.authoredDocuments.downloadAuthoredDocument(id));

      // show toast that archive is getting ready
      if (response.status === 202) {
        store.dispatch(`documentStore/${AddDownloadInProgress}`, id);

        store.commit(`notificationStore/${AddToastNotification}`, {
          type: 'EMAIL_ARCHIVE_PENDING',
          title: "Download request submitted. You will receive a notification when it's ready.",
          notification: { externalId: uuidv4() },
          createdAt: Date.now(),
        });

        return '';
      }

      store.dispatch(`documentStore/${RemoveDownloadInProgress}`, id);
      return response.data.url;
    } catch (error) {
      // Clear progress state on error
      store.dispatch(`documentStore/${RemoveDownloadInProgress}`, id);

      store.commit(`notificationStore/${AddToastNotification}`, {
        type: 'EMAIL_ARCHIVE_ERROR',
        title: 'Failed to request download for Authored Document',
        notification: { externalId: uuidv4() },
        createdAt: Date.now(),
      });

      return '';
    }
  };

  const fetchMediaImages = async ({ query, orgId, filter, from, size }) => {
    try {
      const response = await arcaneApiGateway.post(apiGatewayEndpoints.mediaImages, {
        query,
        orgId,
        filter,
        from,
        size,
      });

      return response.data;
    } catch (error) {
      console.log('Failed to fetch media images', error);
      return [];
    }
  };

  const openDocumentFromTitle = async (payload) => {
    await setSelectedDocument(payload.reference);
    showPreviewDocument(payload.page);
  };

  const duplicateAuthoredDocument = async (parentId) => {
    try {
      const duplicateAuthoredDocument = await arcaneApiGateway.post(
        apiGatewayEndpoints.authoredDocuments.duplicateAuthoredDocument,
        { parentId },
      );
      const conversationId = duplicateAuthoredDocument?.data?.id || '';
      await router.push({ name: Routes.Authoring.name, params: { conversationId: `${conversationId}` } });
    } catch (error) {
      console.log('Failed to duplicate Authored Document', error);
      return null;
    }
  };

  return {
    duplicateAuthoredDocument,
    deleteAuthoredDocumentById,
    getDocumentThumbs,
    getPdfUrl,
    getSourceUrl,
    setSelectedDocument,
    setMessageReferencesThumbs,
    getSourceTranslationAvailable,
    updateSystemMessageTranslationData,
    getAuthoredDocuments,
    setSelectedAuthoredDocument,
    selectedAuthoredDocument,
    authoredDocuments,
    getAuthoredDocumentById,
    selectedReference,
    downloadAuthoredDocument,
    getAuthoredDocumentImportedThumbs,
    fetchMediaImages,
    totalMediaImages,
    setTotalMediaImages,
    openDocumentFromTitle,
  };
};
